import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { Integration } from './types/integration';
import { IntegrationPolicy } from './types/integration-policy';
import { IntegrationWithComputedGlobalVariables } from './types/computed-global-variables';

export class IntegrationApiClient extends ApiClient {
  static serviceName = 'integration-api';
  static scopeName = 'integration-api-v2';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/integration', http, context, options);
  }

  async getIntegrations(
    accountId: string,
    options?: RouteOptions<{
      category?: string | string[];
      states?: string[];
      withTemplates?: boolean;
    }>,
  ): Promise<Integration[]> {
    const { data } = await this.http.get(`/integrations`, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        owner: accountId,
      },
    });
    return data;
  }

  async getIntegrationsPolicies<O = Record<string, string | number | boolean>>(
    owner: string | number,
    options?: RouteOptions,
  ): Promise<IntegrationPolicy<O>[]> {
    return this.http
      .get(`/integrations-policies`, {
        ...defaultAxiosConfig(options),
        params: {
          ...options?.params,
          owner,
        },
      })
      .then(({ data }) => data);
  }

  async computeGlobalVariables(
    accountId: string,
    integrationId: number | string,
    options?: RouteOptions,
  ): Promise<{ public: Record<string, string> }> {
    const { data } = await this.http.get('/compute-gvs', {
      ...defaultAxiosConfig(options),
      params: {
        levels: `ACCOUNT|${accountId}`,
        integrationId: Number(integrationId),
        select: 'PUBLIC',
        decrypted: false,
      },
    });
    return data;
  }

  async getIntegrationWithComputedGlobalVariables(
    accountId: string,
    integrationId: number | string,
    options?: RouteOptions,
  ): Promise<IntegrationWithComputedGlobalVariables> {
    const { data } = await this.http.get(`/integrations/${integrationId}/compute-gvs`, {
      ...defaultAxiosConfig(options),
      params: {
        owner: accountId,
        levels: `ACCOUNT|${accountId}`,
        select: 'ALL',
        decrypted: true,
      },
    });
    return data;
  }
}
